import { z } from "zod";
import { LIMITS } from "./limits";

/**
 * In-app message format — spec `dev/projects/in-app-messages.md` §Data model, §Audience,
 * §Snapshot. The mbls-admin API validates writes with these schemas and the consumer apps
 * parse the retained snapshot with them, so both ends agree on one shape (spec §Security).
 */

/** `ksk`: the kiosk staff screens (KSK-76) — served filtered by the kiosk REST API, not over IoT. */
export const APP_CODES = ["sl2", "dlv2", "ptl", "dsp", "crm", "ksk"] as const;
export const AppCodeSchema = z.enum(APP_CODES);
export type AppCode = z.infer<typeof AppCodeSchema>;

export const LocaleSchema = z.enum(["fr", "en"]);
export type Locale = z.infer<typeof LocaleSchema>;

const localizedText = (max: number) => z.string().trim().min(1).max(max);
/** FR and EN are both required and non-empty after trim (R8). */
export const i18nTextSchema = (max: number) =>
  z.object({ fr: localizedText(max), en: localizedText(max) });
export type I18nText = { fr: string; en: string };

/** `MblsAdminTable` ids start with `001` (R13). Mirrors `idSchema` in `apps/mbls-admin/src/ddb/baseEntity.ts`. */
export const MessageIdSchema = z.string().refine((v) => v.startsWith("001"), {
  message: "Invalid ID format",
});

const isoUtc = z.string().datetime();

// ── Placement (R19) ────────────────────────────────────────────────────────

export type UiSurfaceRef = `UI-${string}-${string}`;
const UiSurfaceRefSchema = z.custom<UiSurfaceRef>(
  (v) => typeof v === "string" && /^UI-[^-]+-.+$/.test(v),
  { message: "Expected a UI surface id `UI-<CODE>-<nnn>`" },
);

export const SIMPLE_PLACEMENT_TYPES = ["top", "below-header", "bottom", "modal", "panel", "feed"] as const;
export const TOAST_CORNERS = ["top-left", "top-right", "bottom-left", "bottom-right"] as const;
export const INLINE_POSITIONS = ["before", "after", "inside-top"] as const;

export const PlacementSchema = z.union([
  z.object({ type: z.enum(SIMPLE_PLACEMENT_TYPES) }),
  z.object({ type: z.literal("toast"), corner: z.enum(TOAST_CORNERS) }),
  z.object({
    type: z.literal("inline"),
    anchor: z.object({ uiSurface: UiSurfaceRefSchema, position: z.enum(INLINE_POSITIONS) }),
  }),
]);
export type Placement = z.infer<typeof PlacementSchema>;
export type PlacementType = Placement["type"];

const smallerDevicePlacement = z.union([PlacementSchema, z.literal("same"), z.literal("hidden")]);
/** `tablet: "same"` = desktop's; `phone: "same"` = tablet's, resolved. */
export const DevicePlacementSchema = z.object({
  desktop: PlacementSchema,
  tablet: smallerDevicePlacement,
  phone: smallerDevicePlacement,
});
export type DevicePlacement = z.infer<typeof DevicePlacementSchema>;

// ── Audience (R12) ─────────────────────────────────────────────────────────

export const OPERATORS = [
  "is",
  "isNot",
  "isOneOf",
  "contains",
  "notContains",
  "matches",
  "eq",
  "gt",
  "lt",
  "between",
  "exists",
  "notExists",
] as const;
export const OperatorSchema = z.enum(OPERATORS);
export type Operator = z.infer<typeof OperatorSchema>;

const STRING_VALUE_OPS: readonly Operator[] = ["is", "isNot", "contains", "notContains", "matches"];
const NUMBER_VALUE_OPS: readonly Operator[] = ["eq", "gt", "lt"];

export const ConditionValueSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.string()),
  z.tuple([z.number(), z.number()]),
]);

/** True when `value` has the shape `op` reads. `exists` / `notExists` ignore the value. */
export function valueFitsOperator(op: Operator, value: unknown): boolean {
  if (STRING_VALUE_OPS.includes(op)) return typeof value === "string";
  if (NUMBER_VALUE_OPS.includes(op)) return typeof value === "number" && Number.isFinite(value);
  if (op === "isOneOf") return Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string");
  if (op === "between")
    return (
      Array.isArray(value) &&
      value.length === 2 &&
      value.every((v) => typeof v === "number" && Number.isFinite(v)) &&
      (value[0] as number) <= (value[1] as number)
    );
  return true;
}

export const ConditionSchema = z
  .object({
    attribute: z.string().min(1),
    op: OperatorSchema,
    value: ConditionValueSchema,
  })
  .superRefine((c, ctx) => {
    if (!valueFitsOperator(c.op, c.value))
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["value"], message: `Value does not fit operator \`${c.op}\`` });
  });
export type Condition = z.infer<typeof ConditionSchema>;

export const ConditionGroupSchema = z.object({
  match: z.enum(["all", "any"]),
  conditions: z.array(ConditionSchema).min(1).max(LIMITS.conditionsPerGroup),
});
export type ConditionGroup = z.infer<typeof ConditionGroupSchema>;

export const AudienceSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("everyone") }),
  z.object({
    mode: z.literal("rules"),
    groups: z.array(ConditionGroupSchema).min(1).max(LIMITS.groupsPerAudience),
  }),
]);
export type Audience = z.infer<typeof AudienceSchema>;

// ── Message ────────────────────────────────────────────────────────────────

/** App route `/…` or `https://…`. `//host` and `/\host` are protocol-relative URLs, not routes. */
export const ActionHrefSchema = z
  .string()
  .refine((h) => h.startsWith("https://") || (h.startsWith("/") && !h.startsWith("//") && !h.startsWith("/\\")), {
    message: "href must be an app route starting with `/` or an `https://` URL",
  });

export const SEVERITIES = ["info", "warning", "outage"] as const;

const messageObject = z.object({
  id: MessageIdSchema,
  appCode: AppCodeSchema,
  kind: z.enum(["banner", "announcement"]),
  status: z.enum(["draft", "live", "ended"]),
  severity: z.enum(SEVERITIES).optional(),
  title: i18nTextSchema(LIMITS.titleChars).optional(),
  body: i18nTextSchema(LIMITS.bodyChars),
  action: z.object({ label: i18nTextSchema(LIMITS.actionLabelChars), href: ActionHrefSchema }).optional(),
  placement: DevicePlacementSchema,
  alsoInFeed: z.boolean(),
  audience: AudienceSchema,
  dismiss: z.enum(["none", "session", "permanent"]),
  frequency: z.enum(["everyVisit", "oncePerBrowser"]),
  pinned: z.boolean(),
  createdAt: isoUtc,
  createdBy: z.string().min(1),
  updatedAt: isoUtc,
  updatedBy: z.string().min(1),
  publishedAt: isoUtc.optional(),
  publishedBy: z.string().min(1).optional(),
  endedAt: isoUtc.optional(),
  endedBy: z.string().min(1).optional(),
});

type RuleFields = { kind: "banner" | "announcement"; severity?: string; title?: unknown; dismiss: string; frequency: string };

/** Cross-field rules of spec §Data model, shared by every message-shaped schema. */
function messageRules(m: RuleFields, ctx: z.RefinementCtx) {
  if (m.kind === "banner" && !m.severity)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["severity"], message: "A banner requires a severity" });
  if (m.kind === "announcement" && m.severity)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["severity"], message: "Only a banner has a severity" });
  if (m.kind === "announcement" && !m.title)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["title"], message: "An announcement requires a title" });
  if (m.severity === "outage" && m.dismiss !== "none")
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dismiss"], message: "An outage cannot be dismissed" });
  // A seen oncePerBrowser message leaves the rendering slots, which would dismiss the outage by itself.
  if (m.severity === "outage" && m.frequency !== "everyVisit")
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["frequency"], message: "An outage shows on every visit" });
}

export const MessageSchema = messageObject.superRefine(messageRules);
export type Message = z.infer<typeof MessageSchema>;

/** API write body: Message minus id, status and audit fields (spec §API). */
export const MessageDraftInputSchema = messageObject
  .omit({
    id: true,
    status: true,
    createdAt: true,
    createdBy: true,
    updatedAt: true,
    updatedBy: true,
    publishedAt: true,
    publishedBy: true,
    endedAt: true,
    endedBy: true,
  })
  .superRefine(messageRules);
export type MessageDraftInput = z.infer<typeof MessageDraftInputSchema>;

// ── Snapshot (R1, R2) ──────────────────────────────────────────────────────

/** Operator identities never leave mbls-admin. */
export const PublicMessageSchema = messageObject
  .omit({
    status: true,
    createdAt: true,
    createdBy: true,
    updatedAt: true,
    updatedBy: true,
    publishedBy: true,
    endedAt: true,
    endedBy: true,
  })
  .superRefine(messageRules);
export type PublicMessage = z.infer<typeof PublicMessageSchema>;

export const SnapshotSchema = z.object({
  schema: z.literal(1),
  appCode: AppCodeSchema,
  /**
   * The app's live-set version (DEV-451): +1 on every change, so a consumer keeps the
   * highest it has seen. Optional: a snapshot published before DEV-451 has none.
   */
  version: z.number().int().nonnegative().optional(),
  publishedAt: isoUtc,
  messages: z.array(PublicMessageSchema),
});
export type Snapshot = z.infer<typeof SnapshotSchema>;
